import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      toast.success("Thanks — we'll be in touch within 2 business days.");
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Email studio@onlyfacts.io instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F3F4F6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Start a project</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] mb-8">
              Tell us what<br />you need <br />to <span className="italic font-serif">show</span>.
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-10">
              We work with organisations across business, government, and the not-for-profit sector to build data products that earn trust and drive action.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                  <Mail size={16} />
                </div>
                <div>
                  <p className="overline mb-1">Email</p>
                  <a
                    href="mailto:studio@onlyfacts.io"
                    data-testid="contact-email-link"
                    className="font-display font-bold text-xl hover:text-[#0052FF] transition-colors"
                  >
                    studio@onlyfacts.io
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                  <MapPin size={16} />
                </div>
                <div>
                  <p className="overline mb-1">Based in</p>
                  <p className="font-display font-bold text-xl">Australia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border-2 border-[#0A0A0A] p-6 md:p-10"
            >
              {sent ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <div className="mx-auto w-14 h-14 bg-[#22C55E] text-white flex items-center justify-center mb-5">
                    <CheckCircle2 size={28} />
                  </div>
                  <h3 className="font-display font-black text-3xl mb-3">Message received.</h3>
                  <p className="text-[#4B5563] mb-6">We'll reply within 2 business days.</p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="border-2 border-[#0A0A0A] px-5 py-2.5 font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors"
                    data-testid="contact-send-another"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <Field label="Your name" required>
                      <input
                        data-testid="contact-input-name"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        className="of-input"
                        placeholder="Jane Doe"
                      />
                    </Field>
                    <Field label="Work email" required>
                      <input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="of-input"
                        placeholder="jane@org.com"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                    <Field label="Organisation">
                      <input
                        data-testid="contact-input-org"
                        type="text"
                        value={form.organisation}
                        onChange={update("organisation")}
                        className="of-input"
                        placeholder="e.g. The Observer"
                      />
                    </Field>
                  </div>

                  <Field label="What are you trying to prove?" required>
                    <textarea
                      data-testid="contact-input-message"
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      className="of-input resize-none"
                      placeholder="Outline your goal, audience and any data you're working with…"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="mt-6 w-full md:w-auto group bg-[#0A0A0A] text-white px-8 py-4 font-medium inline-flex items-center justify-center gap-2 hover:bg-[#0052FF] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Send inquiry"}
                    <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>

                  <p className="mt-4 text-xs text-[#6B7280]">
                    By submitting you agree we can email you back about your project. No spam.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .of-input {
          width: 100%;
          border: 2px solid #0A0A0A;
          background: #FFFFFF;
          padding: 0.75rem 1rem;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 0.95rem;
          color: #0A0A0A;
          transition: background .2s ease, border-color .2s ease;
          border-radius: 0;
          outline: none;
        }
        .of-input:focus {
          background: #FDE047;
        }
        .of-input::placeholder {
          color: #9CA3AF;
        }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">
        {label} {required && <span className="text-[#EF4444]">*</span>}
      </span>
      {children}
    </label>
  );
}
